import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import NavBar from '../components/NavBar';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const { forgotPassword, isLoading, error, clearError } = useAuthStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    try {
      await forgotPassword(email);
      setIsSubmitted(true);
    } catch (err) {
      // Error is handled by the store
    }
  };

  if (isSubmitted) {
    return (
      <div className={`min-h-screen ${tokens.bg.gradient} flex flex-col relative`}>
        <NavBar className={`${tokens.card.bg} shadow-sm`} containerClassName="px-3 sm:px-4">
          <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Runow</h1>
        </NavBar>

        {/* Main Content */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollable">
          <div className="min-h-full flex items-start sm:items-center justify-center p-4">
            <div className={`max-w-md w-full ${tokens.card.bg} rounded-landing ${tokens.card.shadow} p-6 sm:p-8`}>
          <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-6 text-center`}>
            Runow
          </h1>
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-10 w-10 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className={`text-lg font-semibold ${tokens.text.primary} mb-4`}>
              Check Your Email
            </h2>
            <p className={`text-sm ${tokens.text.muted} mb-6`}>
              We've sent a password reset code to <strong className={tokens.text.primary}>{email}</strong>. 
              Please check your inbox and follow the instructions.
            </p>
            <button
              onClick={() => navigate('/reset-password', { state: { email } })}
              className={`w-full ${tokens.button.primary} font-semibold py-3 px-4 transition-colors min-h-[48px]`}
            >
              Enter Reset Code
            </button>
            <div className="mt-4">
              <Link
                to="/login"
                className={`${tokens.text.accent} hover:opacity-80 font-medium text-sm transition-opacity`}
              >
                Back to Sign In
              </Link>
            </div>
          </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} flex flex-col relative`}>
      <NavBar className={`${tokens.card.bg} shadow-sm`} containerClassName="px-3 sm:px-4">
        <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Runow</h1>
      </NavBar>

      {/* Main Content */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollable">
        <div className="min-h-full flex items-start sm:items-center justify-center p-4">
          <div className={`max-w-md w-full ${tokens.card.bg} rounded-landing ${tokens.card.shadow} p-6 sm:p-8`}>
        <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-6 text-center`}>
          Runow
        </h1>
        <h2 className={`text-lg font-semibold ${tokens.text.primary} mb-6 text-center`}>
          Forgot Password
        </h2>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="text-red-800 text-xs">{error}</p>
          </div>
        )}

        <p className={`text-xs ${tokens.text.muted} mb-6 text-center`}>
          Enter your email address and we'll send you a code to reset your password.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="you@example.com"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full ${tokens.button.primary} disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-3 px-4 transition-colors min-h-[48px]`}
          >
            {isLoading ? 'Sending...' : 'Send Reset Code'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className={`${tokens.text.accent} hover:opacity-80 font-medium text-sm transition-opacity`}
          >
            Back to Sign In
          </Link>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}

